// WPS-019/022 · Customer Profile + Smart Card management
// The customer self-service hub: edit CRM info, manage Smart Card URL,
// toggle marketing prefs, regenerate slug (NFC chip security).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileApi, smartCardApi } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import {
  User, Mail, Phone, Briefcase, Building2, Globe, MapPin,
  CreditCard, Sparkles, RotateCcw, Copy, ExternalLink, ArrowLeft, Eye, Download,
} from 'lucide-react';

const Section = ({ label, children }) => (
  <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px' }}>
    <div style={{
      fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
      letterSpacing: '0.18em', textTransform: 'uppercase',
      color: '#64748b', marginBottom: '14px',
    }}>{label}</div>
    {children}
  </div>
);

const Field = ({ label, icon, children }) => (
  <div style={{ marginBottom: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      {icon}
      <Label style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b' }}>
        {label}
      </Label>
    </div>
    {children}
  </div>
);

const CustomerProfilePanel = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('user_token')) {
      navigate('/login');
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    const r = await profileApi.getMine();
    if (r?.success) setProfile(r.data);
    setLoading(false);
  };

  const onChange = (k, v) => setProfile(p => ({ ...p, [k]: v }));
  const onPref = (k, v) => setProfile(p => ({
    ...p,
    marketingPreferences: { ...(p?.marketingPreferences || {}), [k]: v },
  }));

  const save = async () => {
    setSaving(true);
    const payload = {
      fullName: profile.fullName,
      phone: profile.phone,
      jobTitle: profile.jobTitle,
      companyName: profile.companyName,
      website: profile.website,
      address: profile.address,
      industry: profile.industry,
      marketingPreferences: profile.marketingPreferences,
    };
    const r = await profileApi.updateMine(payload);
    setSaving(false);
    if (r?.success) {
      toast({ title: 'Profile saved', description: 'Your CRM profile is up to date.' });
      setProfile(r.data);
    } else {
      toast({ title: 'Save failed', description: 'Try again in a moment.', variant: 'destructive' });
    }
  };

  const rotateSlug = async () => {
    if (!window.confirm('Rotate Smart Card slug?\n\nYour current NFC chip and printed QR will stop working. Reorder new cards or reprogram the chip with the new URL.')) return;
    const r = await profileApi.regenerateSlug();
    if (r?.success) {
      toast({ title: 'Smart Card rotated', description: 'New slug live · old URL disabled.' });
      load();
    } else {
      toast({ title: 'Rotation failed', variant: 'destructive' });
    }
  };

  const copyUrl = () => {
    const url = `https://wunli.com/sc/${profile?.smartCardSlug}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'URL copied', description: url });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: '#64748b' }}>Loading profile…</div>
      </div>
    );
  }
  if (!profile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>Profile not available</div>
          <Button onClick={() => navigate('/dashboard')} variant="outline">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const smartUrl = profile.smartCardSlug ? `https://wunli.com/sc/${profile.smartCardSlug}` : null;
  const vcardUrl = profile.smartCardSlug ? smartCardApi.vcardUrl(profile.smartCardSlug) : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{ background: '#0a0a0a', color: '#ffffff', padding: '20px' }}>
        <div style={{ maxWidth: '640px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'transparent', border: 'none', color: '#c9a961', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
          <div style={{ flex: 1, textAlign: 'right', fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.2em', color: '#c9a961' }}>
            WUNLI · CRM PROFILE
          </div>
        </div>
        <div style={{ maxWidth: '640px', margin: '12px auto 0' }}>
          <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em' }}>Your Profile</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
            CRM record · used for Smart Card updates, renewals, and marketing you opt into.
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '640px', margin: '20px auto 0', padding: '0 16px' }}>
        {/* Smart Card panel */}
        <Section label="Smart Card · Live URL">
          {smartUrl ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', background: '#0a0a0a', color: '#c9a961', fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                <CreditCard className="h-4 w-4" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>{smartUrl}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginTop: '12px' }}>
                <Button onClick={copyUrl} variant="outline" size="sm">
                  <Copy className="h-3 w-3 mr-2" />Copy URL
                </Button>
                <Button onClick={() => window.open(smartUrl, '_blank')} variant="outline" size="sm">
                  <ExternalLink className="h-3 w-3 mr-2" />Preview
                </Button>
                <Button onClick={() => window.open(vcardUrl, '_blank')} variant="outline" size="sm">
                  <Download className="h-3 w-3 mr-2" />vCard
                </Button>
                <Button onClick={rotateSlug} size="sm" style={{ background: '#dc2626', color: '#ffffff' }}>
                  <RotateCcw className="h-3 w-3 mr-2" />Rotate
                </Button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginTop: '16px', padding: '12px', background: '#f8fafc' }}>
                <div>
                  <div style={{ fontSize: '9px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#64748b' }}>Views</div>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: '#01579b' }}>
                    <Eye className="h-4 w-4 inline mr-1" />{profile.smartCardViews || 0}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '9px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#64748b' }}>vCard Saves</div>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: '#01579b' }}>
                    <Download className="h-4 w-4 inline mr-1" />{profile.vcardDownloads || 0}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontSize: '13px', color: '#64748b' }}>Smart Card not provisioned. Create a business card to activate.</div>
          )}
        </Section>

        {/* Identity */}
        <Section label="Identity">
          <Field label="Full Name" icon={<User className="h-3 w-3 text-slate-500" />}>
            <Input value={profile.fullName || ''} onChange={e => onChange('fullName', e.target.value)} />
          </Field>
          <Field label="Email (cannot change)" icon={<Mail className="h-3 w-3 text-slate-500" />}>
            <Input value={profile.email || ''} disabled />
          </Field>
          <Field label="Phone" icon={<Phone className="h-3 w-3 text-slate-500" />}>
            <Input value={profile.phone || ''} onChange={e => onChange('phone', e.target.value)} placeholder="+1 214 949 7999" />
          </Field>
        </Section>

        {/* Business */}
        <Section label="Business">
          <Field label="Job Title" icon={<Briefcase className="h-3 w-3 text-slate-500" />}>
            <Input value={profile.jobTitle || ''} onChange={e => onChange('jobTitle', e.target.value)} />
          </Field>
          <Field label="Company" icon={<Building2 className="h-3 w-3 text-slate-500" />}>
            <Input value={profile.companyName || ''} onChange={e => onChange('companyName', e.target.value)} />
          </Field>
          <Field label="Website" icon={<Globe className="h-3 w-3 text-slate-500" />}>
            <Input value={profile.website || ''} onChange={e => onChange('website', e.target.value)} placeholder="yourbiz.com" />
          </Field>
          <Field label="Address" icon={<MapPin className="h-3 w-3 text-slate-500" />}>
            <Input value={profile.address || ''} onChange={e => onChange('address', e.target.value)} placeholder="Dallas, TX" />
          </Field>
        </Section>

        {/* Marketing preferences */}
        <Section label="Marketing Preferences">
          <ToggleRow label="Email · Smart Card updates" desc="When your card design or content changes, your saved contacts get a quiet update." value={profile.marketingPreferences?.smartCardUpdates ?? true} onToggle={v => onPref('smartCardUpdates', v)} />
          <ToggleRow label="Email · Promotions" desc="Seasonal offers, new tiers, partner perks. ~1 email per month." value={profile.marketingPreferences?.promoOptIn ?? true} onToggle={v => onPref('promoOptIn', v)} />
          <ToggleRow label="Email · Renewal reminders" desc="When it is time to reorder cards · 1 reminder + 1 last-chance." value={profile.marketingPreferences?.renewalReminders ?? true} onToggle={v => onPref('renewalReminders', v)} />
          <ToggleRow label="SMS · Critical only" desc="Production updates · order shipped · usually 1-2 per order." value={profile.marketingPreferences?.smsOptIn ?? false} onToggle={v => onPref('smsOptIn', v)} />
        </Section>

        {/* Save bar */}
        <div style={{ position: 'sticky', bottom: 0, padding: '12px 0', background: 'linear-gradient(180deg, transparent 0%, #f8fafc 30%)' }}>
          <Button onClick={save} disabled={saving} style={{ width: '100%', background: '#01579b', color: '#ffffff', padding: '14px', fontSize: '14px', fontWeight: 700 }}>
            {saving ? 'Saving…' : 'Save Profile'}
          </Button>
          <div style={{ textAlign: 'center', fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', color: '#94a3b8', marginTop: '8px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            <Sparkles className="h-3 w-3 inline mr-1" /> Your data powers Smart Card updates only · we never sell it
          </div>
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, desc, value, onToggle }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{label}</div>
      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', lineHeight: 1.4 }}>{desc}</div>
    </div>
    <Switch checked={value} onCheckedChange={onToggle} />
  </div>
);

export default CustomerProfilePanel;
